import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Braces,
  Clock3,
  Database,
  FileCode2,
  Hourglass,
  Info,
  Plus,
  RadioTower,
  Repeat,
  Save,
  Trash2,
  WandSparkles,
} from 'lucide-react'
import { cgOverlaysApi, cgTemplatesApi } from '../../lib/api.js'
import { appSettingsApi } from '../../lib/mediaBrowserApi.js'
import { isoToParisDateTimeInput, parisDateTimeInputToIso } from '../../lib/paris-date-time.js'
import type { CgOverlay, CgOverlayAnchor, CgOverlayProgramScope, CgOverlayProgramTarget, CgTemplate } from '../../types/index.js'
import { Spinner } from '../common/Spinner.js'
import { CgOverlayControlPanel, type PreviewRule } from './CgOverlayControlPanel.js'
import { CgOverlayProgramScopeSection } from './CgOverlayProgramScopeSection.js'
import { CgOverlayValiditySection, newDraftPeriod, periodInverted, type DraftPeriod } from './CgOverlayValiditySection.js'
import { cgOverlayScopeListLabel, cgOverlayScopeSummary, cgOverlayTargetsPayload } from './cgOverlayProgramScope.js'

type DataEntry = { id: string; key: string; value: string }

type RuleDraft = {
  id?: string
  name: string
  active: boolean
  templateId: string | null
  templatePath: string
  priority: number
  anchor: CgOverlayAnchor
  /** Toujours positif dans le formulaire : c'est l'ancrage qui donne le sens. */
  offsetSec: number
  durationSec: number | null
  repeatIntervalSec: number | null
  /** Retrait anticipe : l'habillage sort avant la bascule antenne. */
  hideBeforeEndSec: number
  nextProgramMaxDelayMin: number
  /** Periodes cumulables ; vide : sans limite de date. */
  validityPeriods: DraftPeriod[]
  /** Programmes concernes : tous, uniquement les cibles, ou tous sauf elles. */
  programScope: CgOverlayProgramScope
  programTargets: CgOverlayProgramTarget[]
  dataEntries: DataEntry[]
}

type TemplateVariable = { key: string; kind?: string; fallback?: string; elementName?: string }

const VARIABLE_LABELS: Record<string, string> = {
  'event.title': 'Titre de l evenement personnalise',
  'event.startAt': 'Date de debut de l evenement',
  'event.endAt': 'Date de fin de l evenement',
}

/**
 * Les quatre logiques de diffusion automatique. L'onglet ne porte plus que de
 * l'automatique : l'envoi a la main se fait depuis la grille, via la coche
 * « afficher sur la grille » de la roue crantee du modele.
 *
 * Elles ne font que trois ancrages : « pendant le programme » se decline en
 * continu ou par intervalles, et c'est la cadence — presente ou absente — qui
 * porte la difference cote base.
 */
type RuleLogic = 'PROGRAM_START' | 'PROGRAM_END' | 'PROGRAM_FULL' | 'PROGRAM_LOOP'

const LOGIC_LABELS: Record<RuleLogic, string> = {
  PROGRAM_START: 'Apres le debut du programme',
  PROGRAM_END:   'Avant la fin du programme',
  PROGRAM_FULL:  'Pendant tout le programme',
  PROGRAM_LOOP:  'Pendant le programme, par intervalles',
}

const LOGIC_HINTS: Record<RuleLogic, string> = {
  PROGRAM_START: 'Un passage, declenche une fois le programme lance.',
  PROGRAM_END:   'Un passage, cale sur la fin du programme.',
  PROGRAM_FULL:  'Un seul passage, tenu a l antenne jusqu a la fin du programme.',
  PROGRAM_LOOP:  'Des passages repetes a cadence fixe jusqu a la fin du programme.',
}

const LOGIC_ANCHORS: Record<RuleLogic, CgOverlayAnchor> = {
  PROGRAM_START: 'PROGRAM_START',
  PROGRAM_END:   'PROGRAM_END',
  PROGRAM_FULL:  'PROGRAM_DURING',
  PROGRAM_LOOP:  'PROGRAM_DURING',
}

/** Sans cadence, un habillage « pendant le programme » reste a l'antenne. */
function ruleLogic(rule: { anchor: CgOverlayAnchor; repeatIntervalSec: number | null }): RuleLogic {
  if (rule.anchor !== 'PROGRAM_DURING') return rule.anchor
  return rule.repeatIntervalSec ? 'PROGRAM_LOOP' : 'PROGRAM_FULL'
}

const DEFAULT_REPEAT_INTERVAL_SEC = 600

function newEntry(key = '', value = ''): DataEntry {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, key, value }
}

function isLogoTemplatePath(htmlPath: string): boolean {
  return htmlPath.replace(/\\/g, '/').replace(/^\.\//, '').startsWith('trafficplay-custom/logos/')
}

function blankRule(logic: RuleLogic, template?: CgTemplate | null): RuleDraft {
  return {
    name: template ? `Diffusion ${template.name}` : 'Nouvelle diffusion',
    active: true,
    templateId: template?.id ?? null,
    templatePath: template?.htmlPath ?? '',
    priority: 0,
    anchor: LOGIC_ANCHORS[logic],
    offsetSec: logic === 'PROGRAM_END' ? 10 : 0,
    // Le continu n'a pas de duree a l'antenne : c'est la fin du programme qui
    // le coupe.
    durationSec: logic === 'PROGRAM_FULL' ? null : 10,
    repeatIntervalSec: logic === 'PROGRAM_LOOP' ? DEFAULT_REPEAT_INTERVAL_SEC : null,
    hideBeforeEndSec: 0,
    nextProgramMaxDelayMin: 5,
    validityPeriods: [],
    programScope: 'ALL',
    programTargets: [],
    dataEntries: [],
  }
}

/** Une regle d'avant les periodes multiples n'a que sa fenetre historique. */
function overlayPeriods(overlay: CgOverlay) {
  if (overlay.validityPeriods?.length) return overlay.validityPeriods
  return overlay.validFrom || overlay.validTo ? [{ startsAt: overlay.validFrom, endsAt: overlay.validTo }] : []
}

function overlayToDraft(overlay: CgOverlay): RuleDraft {
  const data = overlay.data ?? {}
  const entries = Object.entries(data)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .map(([key, value]) => newEntry(key, String(value)))
  return {
    id: overlay.id,
    name: overlay.name,
    active: overlay.active,
    templateId: overlay.templateId,
    templatePath: overlay.templatePath,
    priority: overlay.priority,
    anchor: overlay.anchor,
    offsetSec: Math.abs(overlay.offsetSec),
    durationSec: overlay.durationSec,
    repeatIntervalSec: overlay.repeatIntervalSec,
    hideBeforeEndSec: overlay.hideBeforeEndSec ?? 0,
    nextProgramMaxDelayMin: Number(((overlay.nextProgramMaxDelaySec ?? 300) / 60).toFixed(2)),
    validityPeriods: overlayPeriods(overlay).map(period => newDraftPeriod(period)),
    programScope: overlay.programScope ?? 'ALL',
    programTargets: overlay.programTargets ?? [],
    dataEntries: entries,
  }
}

function draftPayload(channelId: string, draft: RuleDraft) {
  const data = Object.fromEntries(
    draft.dataEntries
      .filter(entry => entry.key.trim())
      .map(entry => {
        const key = entry.key.trim()
        const parsedDate = /(?:At|Date|start|end)$/i.test(key) && entry.value ? new Date(entry.value) : null
        const value = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate.toISOString() : entry.value
        return [key, value]
      }),
  )
  return {
    channelId,
    ...timingPayload(draft),
    name: draft.name.trim(),
    active: draft.active,
    templateId: draft.templateId,
    templatePath: draft.templatePath.trim(),
    priority: draft.priority,
    nextProgramMaxDelaySec: Math.round(Math.max(0, draft.nextProgramMaxDelayMin) * 60),
    data,
    style: {},
  }
}

/**
 * Tout ce qui decide quand la regle sort : envoye a l'enregistrement et a
 * l'apercu des prochains passages, qui calcule donc sur le brouillon.
 */
function timingPayload(draft: RuleDraft): PreviewRule {
  return {
    anchor: draft.anchor,
    // Avant la fin d'un programme se code par un decalage negatif : c'est le
    // seul cas ou le formulaire inverse le signe qu'il affiche.
    offsetSec: draft.anchor === 'PROGRAM_END' ? -Math.abs(draft.offsetSec) : Math.abs(draft.offsetSec),
    // Continu : ni duree ni cadence, l'habillage tient jusqu'a la fin.
    durationSec: ruleLogic(draft) === 'PROGRAM_FULL' ? null : draft.durationSec,
    repeatIntervalSec: ruleLogic(draft) === 'PROGRAM_LOOP'
      ? Math.max(5, draft.repeatIntervalSec ?? DEFAULT_REPEAT_INTERVAL_SEC)
      : null,
    hideBeforeEndSec: Math.max(0, Math.round(draft.hideBeforeEndSec)),
    // Une cible supprimee du catalogue n'est pas renvoyee : l'API l'ecarterait.
    ...cgOverlayTargetsPayload(draft.programScope, draft.programTargets.filter(target => !target.missing)),
    validityPeriods: draft.validityPeriods.map(({ startsAt, endsAt }) => ({ startsAt, endsAt })),
  }
}

/**
 * Le retrait anticipe raccourcit tous les passages. Cale trop tot, il peut meme
 * les avaler : mieux vaut le dire dans le formulaire qu'a l'antenne.
 */
function hideSummary(draft: RuleDraft): string {
  if (!draft.hideBeforeEndSec) return ''
  if (ruleLogic(draft) === 'PROGRAM_END' && draft.offsetSec <= draft.hideBeforeEndSec) {
    return ` Attention : le passage tombe dans les ${draft.hideBeforeEndSec} dernieres secondes, il ne sortira pas.`
  }
  return ` Retire ${draft.hideBeforeEndSec} s avant la fin du programme ou du direct.`
}

function timingSummary(draft: RuleDraft): string {
  const logic = ruleLogic(draft)
  const duration = draft.durationSec ? `${draft.durationSec} s a l'antenne` : "a l'antenne jusqu'a la fin du programme"
  const start = draft.offsetSec === 0
    ? 'Passage des le debut du programme'
    : `Passage ${draft.offsetSec} s apres le debut du programme`
  const interval = Math.max(5, draft.repeatIntervalSec ?? DEFAULT_REPEAT_INTERVAL_SEC)
  const sentence = logic === 'PROGRAM_END' ? `Passage ${draft.offsetSec} s avant la fin du programme, ${duration}`
    : logic === 'PROGRAM_FULL' ? `${start}, a l'antenne jusqu'a la fin du programme`
    : logic === 'PROGRAM_LOOP' ? `${start}, puis toutes les ${interval} s jusqu'a la fin, ${duration}`
    : `${start}, ${duration}`
  return `${sentence}.${hideSummary(draft)}`
}

/**
 * « Uniquement » ou « tous sauf » sans cible encore au catalogue ne
 * s'enregistre pas : l'un ne sortirait jamais, l'autre ne change rien.
 */
function scopeReady(draft: RuleDraft): boolean {
  return draft.programScope === 'ALL' || draft.programTargets.some(target => !target.missing)
}

/** Une periode dont la fin precede le debut bloque l'enregistrement. */
function periodsReady(draft: RuleDraft): boolean {
  return !draft.validityPeriods.some(periodInverted)
}

function draftReady(draft: RuleDraft): boolean {
  return Boolean(draft.name.trim() && draft.templatePath.trim()) && scopeReady(draft) && periodsReady(draft)
}

function templateVariables(template: CgTemplate | null): TemplateVariable[] {
  const raw = template?.defaultData?.variables
  return Array.isArray(raw) ? raw.filter((item): item is TemplateVariable => Boolean(item && typeof item === 'object' && 'key' in item)) : []
}

function isAutomaticVariable(key: string): boolean {
  return key.startsWith('program.') || key.startsWith('channel.') || key.startsWith('clock.')
    || ['title', 'currentTitle', 'nextTitle', 'currentStartTime', 'currentEndTime', 'nextStartTime', 'channelName'].includes(key)
}

function isNextProgramVariable(key: string): boolean {
  return key === 'program.next' || key.startsWith('program.next.')
    || key === 'nextTitle' || key === 'nextStartTime'
}

interface RuleListProps {
  overlays: CgOverlay[]
  selectedId?: string
  loading: boolean
  onSelect: (overlay: CgOverlay) => void
  onCreate: (logic: RuleLogic) => void
}

const RuleList = memo(function RuleList({ overlays, selectedId, loading, onSelect, onCreate }: RuleListProps) {
  return (
    <aside className="cg-rules-list">
      <div className="cg-panel-heading">
        <strong><RadioTower size={14} />Diffusions automatiques</strong>
      </div>
      {/* Le formulaire n'apparait qu'apres le choix d'une logique : c'est ce
          choix qui structure tout le reste du reglage. */}
      <div className="cg-rule-create">
        {(Object.keys(LOGIC_LABELS) as RuleLogic[]).map(logic => (
          <button
            type="button"
            key={logic}
            className="cg-rule-create__button"
            onClick={() => onCreate(logic)}
            title={LOGIC_HINTS[logic]}
          >
            <Plus size={13} />
            <span>{LOGIC_LABELS[logic]}</span>
          </button>
        ))}
      </div>
      <div className="cg-rule-items">
        {loading && <div className="cg-empty"><Spinner /> Chargement...</div>}
        {!loading && overlays.length === 0 && <div className="cg-empty">Aucune diffusion configuree</div>}
        {overlays.map(overlay => (
          <button
            type="button"
            key={overlay.id}
            className={`cg-rule-item${selectedId === overlay.id ? ' is-selected' : ''}`}
            onClick={() => onSelect(overlay)}
          >
            <span className={`cg-status-dot${overlay.active ? ' is-active' : ''}`}><RadioTower size={14} /></span>
            <span>
              <strong>{overlay.name}</strong>
              <small>{LOGIC_LABELS[ruleLogic(overlay)]} · {cgOverlayScopeListLabel(overlay.programScope ?? 'ALL', overlay.programTargets?.length ?? 0)}</small>
            </span>
          </button>
        ))}
      </div>
    </aside>
  )
})

const LOGO_TIMING_MAX_SEC = 60

/**
 * Temporisations des logos antenne, remontees des Reglages : elles decrivent
 * quand un habillage entre et sort, leur place est ici.
 */
const LogoTimings = memo(function LogoTimings() {
  const [liveShowDelaySec, setLiveShowDelaySec] = useState(2)
  const [liveHideBeforeEndSec, setLiveHideBeforeEndSec] = useState(3)
  const [programHideBeforeEndSec, setProgramHideBeforeEndSec] = useState(5)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    appSettingsApi.get().then(settings => {
      setLiveShowDelaySec(settings.liveLogoShowDelaySec ?? 2)
      setLiveHideBeforeEndSec(settings.liveLogoHideBeforeEndSec ?? 3)
      setProgramHideBeforeEndSec(settings.programLogoHideBeforeEndSec ?? 5)
    }).catch(() => {})
  }, [])

  const rows = [
    { label: 'Direct : afficher apres le debut', value: liveShowDelaySec, setValue: setLiveShowDelaySec },
    { label: 'Direct : masquer avant la fin', value: liveHideBeforeEndSec, setValue: setLiveHideBeforeEndSec },
    { label: 'Programme : masquer avant la fin', value: programHideBeforeEndSec, setValue: setProgramHideBeforeEndSec },
  ]

  const save = useCallback(async () => {
    setSaving(true)
    setMessage('')
    try {
      await appSettingsApi.update({
        liveLogoShowDelaySec: liveShowDelaySec,
        liveLogoHideBeforeEndSec: liveHideBeforeEndSec,
        programLogoHideBeforeEndSec: programHideBeforeEndSec,
      })
      setMessage('Temporisations enregistrees')
    } catch (error: any) {
      setMessage(error.message ?? 'Erreur de sauvegarde')
    } finally {
      setSaving(false)
    }
  }, [liveShowDelaySec, liveHideBeforeEndSec, programHideBeforeEndSec])

  return (
    <section className="cg-form-section">
      <h3><Clock3 size={14} />Temporisation des logos antenne</h3>
      <div className="cg-field-grid three">
        {rows.map(row => (
          <label className="cg-field" key={row.label}>
            <span>{row.label} (s)</span>
            <input
              type="number"
              min={0}
              disabled={saving}
              max={LOGO_TIMING_MAX_SEC}
              step={0.5}
              value={row.value}
              onChange={event => row.setValue(Math.min(LOGO_TIMING_MAX_SEC, Math.max(0, Number(event.target.value))))}
            />
          </label>
        ))}
      </div>
      <div className="cg-toolbar-actions">
        <button type="button" className="cg-button is-primary" onClick={save} disabled={saving}>
          <Save size={14} />{saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        {message && <span className="cg-inline-message"><Info size={12} />{message}</span>}
      </div>
    </section>
  )
})

interface VariableRowsProps {
  variables: TemplateVariable[]
  entries: DataEntry[]
  onChange: (entries: DataEntry[]) => void
}

const VariableRows = memo(function VariableRows({ variables, entries, onChange }: VariableRowsProps) {
  const automatic = variables.filter(variable => isAutomaticVariable(variable.key))
  const ensureCustomVariables = useMemo(() => variables.filter(variable => !isAutomaticVariable(variable.key)), [variables])

  useEffect(() => {
    const missing = ensureCustomVariables.filter(variable => !entries.some(entry => entry.key === variable.key))
    if (missing.length) onChange([...entries, ...missing.map(variable => newEntry(variable.key, variable.fallback ?? ''))])
  }, [ensureCustomVariables, entries, onChange])

  const patchEntry = useCallback((id: string, patch: Partial<DataEntry>) => {
    onChange(entries.map(entry => entry.id === id ? { ...entry, ...patch } : entry))
  }, [entries, onChange])

  const removeEntry = useCallback((id: string) => {
    onChange(entries.filter(entry => entry.id !== id))
  }, [entries, onChange])

  return (
    <section className="cg-form-section">
      <div className="cg-section-heading">
        <h3><Database size={14} />Données</h3>
        <button type="button" className="cg-icon-button" onClick={() => onChange([...entries, newEntry()])} title="Ajouter une donnée" aria-label="Ajouter une donnée"><Plus size={14} /></button>
      </div>
      {automatic.length > 0 && (
        <div className="cg-variable-summary">
          {automatic.map(variable => <span key={variable.key}><Braces size={11} />{variable.elementName || variable.key}</span>)}
        </div>
      )}
      <div className="cg-data-rows">
        {entries.map(entry => {
          const variable = variables.find(item => item.key === entry.key)
          const isDate = variable?.kind === 'datetime' || /(?:At|Date|start|end)$/i.test(entry.key)
          const variableLabel = VARIABLE_LABELS[entry.key] || variable?.elementName || entry.key
          return (
            <div className="cg-data-row" key={entry.id}>
              {variable
                ? <div className="cg-data-variable"><strong>{variableLabel}</strong><small>{entry.key}</small></div>
                : <input value={entry.key} onChange={event => patchEntry(entry.id, { key: event.target.value })} placeholder="variable" />}
              <input
                type={isDate ? 'datetime-local' : 'text'}
                value={isDate ? isoToParisDateTimeInput(entry.value) : entry.value}
                onChange={event => patchEntry(entry.id, { value: isDate ? parisDateTimeInputToIso(event.target.value) : event.target.value })}
                placeholder="valeur"
              />
              <button type="button" className="cg-icon-button is-danger" onClick={() => removeEntry(entry.id)} title="Supprimer" aria-label="Supprimer"><Trash2 size={13} /></button>
            </div>
          )
        })}
      </div>
    </section>
  )
})

interface Props {
  active?: boolean
  channelId: string
  preferredTemplateId: string | null
  templatesVersion: number
  onPreferredTemplateConsumed: () => void
}

export const CgOverlayRulesPanel = memo(function CgOverlayRulesPanel({
  active = true,
  channelId,
  preferredTemplateId,
  templatesVersion,
  onPreferredTemplateConsumed,
}: Props) {
  const [overlays, setOverlays] = useState<CgOverlay[]>([])
  const [templates, setTemplates] = useState<CgTemplate[]>([])
  // Rien a l'arrivee : l'encart de reglage ne s'ouvre qu'une fois une logique
  // de diffusion choisie, ou une regle existante selectionnee.
  const [draft, setDraft] = useState<RuleDraft | null>(null)
  const ruleSession = useRef(0)
  const draftRef = useRef(draft)
  draftRef.current = draft
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [nextOverlays, nextTemplates] = await Promise.all([
        cgOverlaysApi.list(channelId),
        cgTemplatesApi.list(channelId),
      ])
      setOverlays(nextOverlays)
      setTemplates(nextTemplates.filter(template => template.active && !isLogoTemplatePath(template.htmlPath)))
    } finally {
      setLoading(false)
    }
  }, [channelId])

  useEffect(() => { if (active) void load().catch(error => setMessage(error.message ?? 'Chargement impossible')) }, [active, load, templatesVersion])

  useEffect(() => {
    if (!preferredTemplateId || templates.length === 0) return
    const template = templates.find(item => item.id === preferredTemplateId)
    if (!template) return
    ruleSession.current++
    setDraft(blankRule('PROGRAM_START', template))
    setMessage('')
    onPreferredTemplateConsumed()
  }, [preferredTemplateId, templates, onPreferredTemplateConsumed])

  const selectedTemplate = useMemo(
    () => templates.find(template => template.id === draft?.templateId) ?? null,
    [templates, draft?.templateId],
  )
  const variables = useMemo(() => templateVariables(selectedTemplate), [selectedTemplate])
  const draftLogic = draft ? ruleLogic(draft) : 'PROGRAM_START'
  const usesNextProgram = useMemo(
    () => variables.some(variable => isNextProgramVariable(variable.key))
      || (draft?.dataEntries ?? []).some(entry => entry.key.endsWith('Source') && isNextProgramVariable(entry.value)),
    [draft?.dataEntries, variables],
  )

  const patch = useCallback(<K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) => {
    setDraft(previous => previous ? { ...previous, [key]: value } : previous)
  }, [])

  const createRule = useCallback((logic: RuleLogic) => {
    if (saving) return
    ruleSession.current++
    setDraft(blankRule(logic, selectedTemplate))
    setMessage('')
  }, [selectedTemplate, saving])

  const selectTemplate = useCallback((id: string) => {
    const template = templates.find(item => item.id === id) ?? null
    setDraft(previous => previous ? {
      ...previous,
      templateId: template?.id ?? null,
      templatePath: template?.htmlPath ?? previous.templatePath,
      name: previous.id || !template ? previous.name : `Diffusion ${template.name}`,
    } : previous)
  }, [templates])

  const selectLogic = useCallback((logic: RuleLogic) => {
    setDraft(previous => previous ? {
      ...previous,
      anchor: LOGIC_ANCHORS[logic],
      // Passer en continu efface la duree a l'antenne, en sortir la retablit :
      // sans ca l'habillage disparaitrait apres 10 s ou resterait pour toujours.
      durationSec: logic === 'PROGRAM_FULL' ? null
        : ruleLogic(previous) === 'PROGRAM_FULL' ? 10
        : previous.durationSec,
      repeatIntervalSec: logic === 'PROGRAM_LOOP'
        ? previous.repeatIntervalSec ?? DEFAULT_REPEAT_INTERVAL_SEC
        : null,
    } : previous)
  }, [])

  const save = useCallback(async () => {
    if (saving || !draft || !draftReady(draft)) return
    const session = ruleSession.current
    setSaving(true)
    setMessage('')
    try {
      const payload = draftPayload(channelId, draft)
      const saved = draft.id
        ? await cgOverlaysApi.update(draft.id, payload)
        : await cgOverlaysApi.create(payload)
      setOverlays(current => [...current.filter(item => item.id !== saved.id), saved])
      if (session !== ruleSession.current) return
      if (draftRef.current === draft) {
        setDraft(overlayToDraft(saved)); setMessage('Diffusion enregistrée')
      } else if (draftRef.current?.id === draft.id) {
        setDraft(current => current ? { ...current, id: saved.id } : current)
        setMessage('Version envoyée enregistrée — nouvelles modifications à enregistrer')
      }
    } catch (error: any) {
      if (session === ruleSession.current) setMessage(error.message ?? 'Erreur de sauvegarde')
    } finally {
      setSaving(false)
    }
  }, [channelId, draft, saving])

  const remove = useCallback(async () => {
    if (saving || !draft?.id || !window.confirm(`Supprimer la diffusion « ${draft.name} » ?`)) return
    const session = ruleSession.current
    setSaving(true)
    try {
      await cgOverlaysApi.delete(draft.id)
      setOverlays(current => current.filter(item => item.id !== draft.id))
      if (session === ruleSession.current) { setDraft(null); setMessage('Diffusion supprimée') }
    } catch (error: any) {
      setMessage(error.message ?? 'Erreur de suppression')
    } finally {
      setSaving(false)
    }
  }, [draft?.id, draft?.name, load, saving])

  const setEntries = useCallback((entries: DataEntry[]) => patch('dataEntries', entries), [patch])
  const setProgramScope = useCallback((scope: CgOverlayProgramScope) => patch('programScope', scope), [patch])
  const setProgramTargets = useCallback((targets: CgOverlayProgramTarget[]) => patch('programTargets', targets), [patch])
  const setPeriods = useCallback((periods: DraftPeriod[]) => patch('validityPeriods', periods), [patch])
  const previewRule = useMemo(() => draft ? timingPayload(draft) : null, [draft])

  return (
    <div className={`cg-rules-workspace${draft ? ' has-control' : ''}`}>
      <RuleList overlays={overlays} selectedId={draft?.id} loading={loading} onSelect={overlay => { if (!saving) { ruleSession.current++; setDraft(overlayToDraft(overlay)) } }} onCreate={createRule} />
      <div className={`cg-rule-editor${draft ? '' : ' is-empty'}`}>
        {!draft ? (
          <div className="cg-rule-form">
            <div className="cg-empty cg-rule-placeholder">
              <RadioTower size={18} />
              <strong>Diffusion automatique des habillages</strong>
              <span>Choisissez une logique de diffusion a gauche, ou selectionnez une regle existante. L'envoi a la main se fait depuis la grille.</span>
            </div>
            {active && <LogoTimings />}
          </div>
        ) : (
          <>
            <div className="cg-rule-toolbar">
              <div className="cg-rule-title">
                <span className={`cg-rule-title-icon${draft.id && draft.active ? ' is-active' : ''}`}><RadioTower size={15} /></span>
                <span>
                  <strong>{draft.id ? draft.name : 'Nouvelle diffusion'}</strong>
                  {/* L'en-tete resume la regle : on sait ce qu'on edite sans
                      relire le formulaire. */}
                  <small className="cg-rule-recap">
                    <span>{draft.id ? (draft.active ? 'Active' : 'Inactive') : 'Non enregistrée'}</span>
                    <span>{selectedTemplate ? selectedTemplate.name : 'Chemin HTML'}</span>
                    {selectedTemplate?.cgLayer != null && <span>Layer CG {selectedTemplate.cgLayer}</span>}
                    <span>{cgOverlayScopeListLabel(draft.programScope, draft.programTargets.length)}</span>
                  </small>
                </span>
                {message && <span className="cg-inline-message"><Info size={12} />{message}</span>}
              </div>
              <div className="cg-toolbar-actions">
                {draft.id && <button type="button" className="cg-icon-button is-danger" onClick={remove} disabled={saving} title="Supprimer la diffusion" aria-label="Supprimer la diffusion"><Trash2 size={14} /></button>}
                <button type="button" className="cg-button is-primary" onClick={save} disabled={saving || !draftReady(draft)}>
                  <Save size={14} />{saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>

            <div className="cg-rule-form cg-rule-form--stack">
              <section className="cg-form-section">
                <h3><FileCode2 size={14} />Identité et modèle</h3>
                <div className="cg-field-grid">
                  <label className="cg-field"><span>Nom</span><input value={draft.name} onChange={event => patch('name', event.target.value)} /></label>
                  <label className="cg-field"><span>Modèle</span>
                    <select value={draft.templateId ?? ''} onChange={event => selectTemplate(event.target.value)}>
                      <option value="">Chemin manuel</option>
                      {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
                    </select>
                  </label>
                </div>
                {/* Le layer n'est plus saisi : il est attribue au modele a sa
                    creation, et rappele dans l'en-tete. Le chemin ne se saisit
                    qu'a defaut de modele. */}
                {!draft.templateId && (
                  <label className="cg-field"><span>Chemin HTML</span><input value={draft.templatePath} onChange={event => patch('templatePath', event.target.value)} /></label>
                )}
                <label className="cg-check cg-toggle"><input type="checkbox" checked={draft.active} onChange={event => patch('active', event.target.checked)} /><span aria-hidden="true" />Diffusion active</label>
              </section>

              <section className="cg-form-section" aria-labelledby="cg-logic-title">
                <h3 id="cg-logic-title"><WandSparkles size={14} />Déclenchement</h3>
                {/* Les quatre logiques restent visibles, avec leur explication :
                    c'est le choix qui structure tout le reste du reglage. */}
                <div className="cg-logic-grid" role="radiogroup" aria-label="Déclenchement">
                  {(Object.keys(LOGIC_LABELS) as RuleLogic[]).map(logic => (
                    <button
                      type="button"
                      key={logic}
                      role="radio"
                      aria-checked={draftLogic === logic}
                      className={`cg-logic-card${draftLogic === logic ? ' is-active' : ''}`}
                      onClick={() => selectLogic(logic)}
                    >
                      <span className="cg-logic-card__icon" aria-hidden="true">
                        {logic === 'PROGRAM_LOOP' ? <Repeat size={14} /> : logic === 'PROGRAM_FULL' ? <RadioTower size={14} /> : logic === 'PROGRAM_END' ? <Hourglass size={14} /> : <Clock3 size={14} />}
                      </span>
                      <span>
                        <strong>{LOGIC_LABELS[logic]}</strong>
                        <small>{LOGIC_HINTS[logic]}</small>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="cg-field-grid three">
                  <label className="cg-field">
                    <span>{draftLogic === 'PROGRAM_END' ? 'Avant la fin (s)' : 'Apres le debut (s)'}</span>
                    <input type="number" min="0" value={draft.offsetSec} onChange={event => patch('offsetSec', Math.max(0, Number(event.target.value)))} />
                  </label>
                  {/* En continu, la duree ne se regle pas : la fin du programme
                      coupe l'habillage. */}
                  {draftLogic !== 'PROGRAM_FULL' && (
                    <label className="cg-field"><span>Duree a l'antenne (s)</span><input type="number" min="1" value={draft.durationSec ?? ''} onChange={event => patch('durationSec', event.target.value ? Number(event.target.value) : null)} placeholder="Jusqu'a la fin" /></label>
                  )}
                  {draftLogic === 'PROGRAM_LOOP' && (
                    <label className="cg-field"><span>Intervalle (s)</span><input type="number" min="5" step="5" value={draft.repeatIntervalSec ?? DEFAULT_REPEAT_INTERVAL_SEC} onChange={event => patch('repeatIntervalSec', Math.max(5, Number(event.target.value)))} /></label>
                  )}
                  {/* Retrait anticipe : le meme reglage vaut pour un programme
                      comme pour un direct, c'est leur fin qui sert de repere. */}
                  <label className="cg-field">
                    <span>Retirer avant la fin (s)</span>
                    <input
                      type="number"
                      min="0"
                      value={draft.hideBeforeEndSec}
                      onChange={event => patch('hideBeforeEndSec', Math.max(0, Number(event.target.value)))}
                      placeholder="0"
                    />
                  </label>
                </div>
                {usesNextProgram && (
                  <label className="cg-field">
                    <span>Recherche du programme suivant (min)</span>
                    <input
                      type="number"
                      min="0"
                      max="1440"
                      step="0.5"
                      value={draft.nextProgramMaxDelayMin}
                      onChange={event => patch('nextProgramMaxDelayMin', Math.min(1440, Math.max(0, Number(event.target.value))))}
                    />
                  </label>
                )}
                <div className="cg-timing-summary">
                  <Info size={13} />
                  <span>
                    {timingSummary(draft)}
                    {usesNextProgram && ` Programme ou direct suivant recherché jusqu'à ${draft.nextProgramMaxDelayMin} min après la fin du programme courant.`}
                  </span>
                </div>
              </section>

              <CgOverlayProgramScopeSection
                active={active}
                channelId={channelId}
                scope={draft.programScope}
                targets={draft.programTargets}
                onScopeChange={setProgramScope}
                onTargetsChange={setProgramTargets}
              />

              <CgOverlayValiditySection periods={draft.validityPeriods} onChange={setPeriods} />

              <VariableRows variables={variables} entries={draft.dataEntries} onChange={setEntries} />
            </div>
          </>
        )}
      </div>
      {draft && previewRule && (
        <CgOverlayControlPanel
          active={active}
          channelId={channelId}
          template={selectedTemplate}
          rule={previewRule}
          ruleReady={scopeReady(draft) && periodsReady(draft)}
          timingSentence={timingSummary(draft)}
          scopeSentence={cgOverlayScopeSummary(draft.programScope, draft.programTargets)}
        />
      )}
    </div>
  )
})
